// ════════════════════════════════════════════════════════════════════════
//  Die Arbeitsfläche: links die Liste, rechts das Detail
//
//  Vorher waren Lektionen und Grundlagen zwei Bereiche, und innerhalb der
//  Lektionen klappte man Karten auf und zu. Beides zusammen hieß: um von einer
//  Lektion zu der Taste zu kommen, die ihr fehlt, wechselte man den Bereich,
//  scrollte und klappte – bei zehn Lektionen dreimal am Tag.
//
//  Die Liste führt deshalb ALLES, woran man am Lehrgang arbeitet: Einstellungen,
//  Lektionen, Fassungen. Es gibt keine Unternavigation mehr darüber – sie hätte
//  dieselben Ziele ein zweites Mal angeboten, und zwei Wege zum selben Ort sind
//  keine Auswahl, sondern eine Frage.
// ════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { coursePath, useCourseSlug } from "../../../shared/lib/coursePath.js";
import { moveLesson, moveLessonTo } from "../../lessons/lib/edit.js";
import { problemsAt } from "../../validation/lib/validation.js";
import own from "./draft.module.css";

export function Workspace() {
  const slug = useCourseSlug();
  const at = (rest: string) => coursePath(slug, rest);
  const { bundle, setBundle, problems, readOnly } = useDraft();
  // Was gerade gezogen wird und worüber es schwebt. Beides nur für die Anzeige –
  // geschrieben wird erst beim Loslassen.
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `${own.sideLink} ${isActive ? own.sideActive : ""}`;

  // Es gibt weder einen Entwurf noch eine veröffentlichte Fassung – der Fall
  // tritt genau einmal ein, vor der allerersten. Zwei Einträge, keine
  // Überschriften: für zwei Zeilen wären sie Gliederung ohne Inhalt.
  if (!bundle) {
    return (
      <div className={own.workspace}>
        <nav className={own.side} aria-label="Lehrgang">
          <NavLink to={at("uebersicht")} className={linkClass}>
            <span className={own.sideName}>Überblick</span>
          </NavLink>
          <NavLink to={at("fassungen")} className={linkClass}>
            <span className={own.sideName}>Fassungen</span>
          </NavLink>
        </nav>
        <div className={own.detail}>
          <Outlet />
        </div>
      </div>
    );
  }

  // Zwei Grundlagenseiten, zwei Pfadgruppen. Die Zuordnung steht hier und in
  // problemTarget.ts – beide müssen dasselbe meinen, sonst zeigt die Liste eine
  // Zahl, die auf der Zielseite nicht auftaucht.
  const keyboardProblems = problems.filter(
    (p) => p.path.startsWith("fingers.") || p.path.startsWith("keyboard."),
  );
  const starProblems = problemsAt(problems, "starThresholds");
  const testProblems = problems.filter((p) => p.path.startsWith("assessment"));

  return (
    <div className={own.workspace}>
      <nav className={own.side} aria-label="Lehrgang">
        <p className={own.sideHead}>Einstellungen</p>
        <NavLink to={at("uebersicht")} className={linkClass}>
          <span className={own.sideName}>Überblick</span>
        </NavLink>
        <NavLink to={at("stammdaten")} className={linkClass}>
          <span className={own.sideName}>Stammdaten</span>
        </NavLink>
        <NavLink to={at("grundlagen/tastatur")} className={linkClass}>
          <span className={own.sideName}>Tastatur und Finger</span>
          {keyboardProblems.length > 0 && (
            <span className={own.sideCount}>{keyboardProblems.length}</span>
          )}
        </NavLink>
        <NavLink to={at("grundlagen/sterne")} className={linkClass}>
          <span className={own.sideName}>Sterne</span>
          {starProblems.length > 0 && (
            <span className={own.sideCount}>{starProblems.length}</span>
          )}
        </NavLink>

        <NavLink to={at("grundlagen/test")} className={linkClass}>
          <span className={own.sideName}>Test</span>
          {testProblems.length > 0 && (
            <span className={own.sideCount}>{testProblems.length}</span>
          )}
        </NavLink>

        <p className={own.sideHead}>Lektionen</p>
        {!readOnly && bundle.lessons.length > 1 && (
          <p className={own.sideHint}>Zum Umsortieren ziehen</p>
        )}
        {bundle.lessons.map((lesson, index) => {
          const notices = problemsAt(problems, `lessons.${lesson.key}`);
          return (
            <NavLink
              key={lesson.key}
              to={at(`lektionen/${lesson.key}`)}
              className={({ isActive }) =>
                [
                  linkClass({ isActive }),
                  dragging === lesson.key ? own.sideDragging : "",
                  over === lesson.key && dragging !== lesson.key ? own.sideOver : "",
                ].join(" ")
              }
              // Ziehen nur im Entwurf. An einer veröffentlichten Fassung wäre
              // die Reihenfolge eine Änderung wie jede andere.
              draggable={!readOnly}
              onDragStart={(e) => {
                setDragging(lesson.key);
                // Ohne Nutzlast bricht Firefox das Ziehen sofort ab.
                e.dataTransfer.setData("text/plain", lesson.key);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onDragOver={(e) => {
                if (!dragging) return;
                // Ohne preventDefault lehnt der Browser das Fallenlassen ab –
                // der Mauszeiger zeigt dann dauerhaft das Verbotsschild.
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setOver(lesson.key);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging && dragging !== lesson.key) {
                  setBundle(moveLessonTo(bundle, dragging, index));
                }
                setDragging(null);
                setOver(null);
              }}
              // Ziehen allein wäre nur mit der Maus zu bedienen. Alt+Pfeil tut
              // dasselbe von der Tastatur aus; sichtbare Pfeilknöpfe gibt es
              // dafür bewusst nicht mehr.
              onKeyDown={(e) => {
                if (readOnly || !e.altKey) return;
                if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                e.preventDefault();
                setBundle(moveLesson(bundle, lesson.key, e.key === "ArrowUp" ? -1 : 1));
              }}
              title={
                readOnly
                  ? undefined
                  : "Ziehen zum Umsortieren – oder Alt + Pfeil hoch/runter"
              }
            >
              <span className={own.sideDot} style={{ background: lesson.color }} />
              <span className={own.sideName}>
                {lesson.number}. {lesson.title}
              </span>
              {notices.length > 0 ? (
                <span className={own.sideCount}>{notices.length}</span>
              ) : (
                <span className={own.sideQuiet}>{lesson.stages.length}</span>
              )}
            </NavLink>
          );
        })}
        {/* Anlegen geht nur im Entwurf. Der Weg dorthin steht in der Leiste –
            ein zweiter Knopf hier führte zu der Frage, welcher der richtige ist. */}
        {!readOnly && (
          <NavLink
            to={at("lektionen/neu")}
            className={`${own.sideNew}`}
            data-tour="neue-lektion"
          >
            + Neue Lektion
          </NavLink>
        )}

        {/* Kein Einstellungspunkt: die Fassungsliste ändert nichts, sie zeigt
            die Geschichte. Deshalb steht sie für sich, unten. */}
        <p className={own.sideHead}>Verlauf</p>
        <NavLink to={at("fassungen")} className={linkClass}>
          <span className={own.sideName}>Fassungen</span>
        </NavLink>
      </nav>

      <div className={own.detail}>
        <Outlet />
      </div>
    </div>
  );
}
